import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchTeamStats, fetchTeamPlayedMaps } from '../../services/TeamService';
import { TeamMatchStats } from '../../models/TeamMatchStats';
import SideSelector, { Side } from '../../components/SideSelector/SideSelector';
import Loading from '../../components/Loading';
import { mapMappings } from '../../Constants';
import BuySiteWinRatePanel from '../../components/BuySiteWinRatePanel/BuySiteWinRatePanel';
import '../../styles/theme.css';
import './TeamStats.css';

interface TeamStatsProps {
    season: number;
    /** The squad's canonical id; defaults to the URL's. */
    teamId?: string;
}

interface SideData {
    roundsPlayed: number;
    roundsWon: number;
    roundsWithFirstKill: number;
    roundsWonAfterFirstKill: number;
    roundsWithFirstDeath: number;
    roundsWonAfterFirstDeath: number;
    pistolRoundsPlayed: number;
    pistolRoundsWon: number;
    secondRoundsPlayed: number;
    secondRoundsWon: number;
    roundsWithTeamAwp: number;
    roundsWonWithTeamAwp: number;
    roundsWithoutTeamAwp: number;
    roundsWonWithoutTeamAwp: number;
    roundsVsEnemyAwp: number;
    roundsWonVsEnemyAwp: number;
    roundsVsNoEnemyAwp: number;
    roundsWonVsNoEnemyAwp: number;
    totalEnemiesFlashed: number;
    totalEnemyFlashDuration: number;
    totalTradesReceived: number;
    clutchesAttempted: number;
    clutchesWon: number;
    totalDeaths: number;
    roundsWith3K: number;
    roundsWith4K: number;
    roundsWith5K: number;
    roundsOnFullBuy: number;
    roundsOnFullBuyWon: number;
    roundsOnHalfBuy: number;
    roundsOnHalfBuyWon: number;
    roundsOnEco: number;
    roundsOnEcoWon: number;
    roundsVsFullBuy: number;
    roundsVsFullBuyWon: number;
    roundsVsHalfBuy: number;
    roundsVsHalfBuyWon: number;
    roundsVsEco: number;
    roundsVsEcoWon: number;
    // Post-plant (side-specific naming differs)
    bombPlantsFor: number;
    roundsWonAfterPlant: number;
    bombPlantsAgainst: number;
    roundsWonAfterEnemyPlant: number;
}

function getSideStats(stats: TeamMatchStats, side: Side): SideData {
    if (side === 't') {
        return {
            roundsPlayed: stats.tSideRoundsPlayed,
            roundsWon: stats.tSideRoundsWon,
            roundsWithFirstKill: stats.tSideRoundsWithFirstKill,
            roundsWonAfterFirstKill: stats.tSideRoundsWonAfterFirstKill,
            roundsWithFirstDeath: stats.tSideRoundsWithFirstDeath,
            roundsWonAfterFirstDeath: stats.tSideRoundsWonAfterFirstDeath,
            pistolRoundsPlayed: stats.tSidePistolRoundsPlayed,
            pistolRoundsWon: stats.tSidePistolRoundsWon,
            secondRoundsPlayed: stats.tSideSecondRoundsPlayed,
            secondRoundsWon: stats.tSideSecondRoundsWon,
            roundsWithTeamAwp: stats.tSideRoundsWithTeamAwp,
            roundsWonWithTeamAwp: stats.tSideRoundsWonWithTeamAwp,
            roundsWithoutTeamAwp: stats.tSideRoundsWithoutTeamAwp,
            roundsWonWithoutTeamAwp: stats.tSideRoundsWonWithoutTeamAwp,
            roundsVsEnemyAwp: stats.tSideRoundsVsEnemyAwp,
            roundsWonVsEnemyAwp: stats.tSideRoundsWonVsEnemyAwp,
            roundsVsNoEnemyAwp: stats.tSideRoundsVsNoEnemyAwp,
            roundsWonVsNoEnemyAwp: stats.tSideRoundsWonVsNoEnemyAwp,
            totalEnemiesFlashed: stats.tSideTotalEnemiesFlashed,
            totalEnemyFlashDuration: stats.tSideTotalEnemyFlashDuration,
            totalTradesReceived: stats.tSideTotalTradesReceived,
            clutchesAttempted: stats.tSideClutchesAttempted,
            clutchesWon: stats.tSideClutchesWon,
            totalDeaths: stats.tSideTotalDeaths,
            roundsWith3K: stats.tSideRoundsWith3K,
            roundsWith4K: stats.tSideRoundsWith4K,
            roundsWith5K: stats.tSideRoundsWith5K,
            roundsOnFullBuy: stats.tSideRoundsOnFullBuy,
            roundsOnFullBuyWon: stats.tSideRoundsOnFullBuyWon,
            roundsOnHalfBuy: stats.tSideRoundsOnHalfBuy,
            roundsOnHalfBuyWon: stats.tSideRoundsOnHalfBuyWon,
            roundsOnEco: stats.tSideRoundsOnEco,
            roundsOnEcoWon: stats.tSideRoundsOnEcoWon,
            roundsVsFullBuy: stats.tSideRoundsVsFullBuy,
            roundsVsFullBuyWon: stats.tSideRoundsVsFullBuyWon,
            roundsVsHalfBuy: stats.tSideRoundsVsHalfBuy,
            roundsVsHalfBuyWon: stats.tSideRoundsVsHalfBuyWon,
            roundsVsEco: stats.tSideRoundsVsEco,
            roundsVsEcoWon: stats.tSideRoundsVsEcoWon,
            bombPlantsFor: stats.tSideBombPlants,
            roundsWonAfterPlant: stats.tSideRoundsWonAfterPlant,
            bombPlantsAgainst: 0,
            roundsWonAfterEnemyPlant: 0,
        };
    }
    if (side === 'ct') {
        return {
            roundsPlayed: stats.ctSideRoundsPlayed,
            roundsWon: stats.ctSideRoundsWon,
            roundsWithFirstKill: stats.ctSideRoundsWithFirstKill,
            roundsWonAfterFirstKill: stats.ctSideRoundsWonAfterFirstKill,
            roundsWithFirstDeath: stats.ctSideRoundsWithFirstDeath,
            roundsWonAfterFirstDeath: stats.ctSideRoundsWonAfterFirstDeath,
            pistolRoundsPlayed: stats.ctSidePistolRoundsPlayed,
            pistolRoundsWon: stats.ctSidePistolRoundsWon,
            secondRoundsPlayed: stats.ctSideSecondRoundsPlayed,
            secondRoundsWon: stats.ctSideSecondRoundsWon,
            roundsWithTeamAwp: stats.ctSideRoundsWithTeamAwp,
            roundsWonWithTeamAwp: stats.ctSideRoundsWonWithTeamAwp,
            roundsWithoutTeamAwp: stats.ctSideRoundsWithoutTeamAwp,
            roundsWonWithoutTeamAwp: stats.ctSideRoundsWonWithoutTeamAwp,
            roundsVsEnemyAwp: stats.ctSideRoundsVsEnemyAwp,
            roundsWonVsEnemyAwp: stats.ctSideRoundsWonVsEnemyAwp,
            roundsVsNoEnemyAwp: stats.ctSideRoundsVsNoEnemyAwp,
            roundsWonVsNoEnemyAwp: stats.ctSideRoundsWonVsNoEnemyAwp,
            totalEnemiesFlashed: stats.ctSideTotalEnemiesFlashed,
            totalEnemyFlashDuration: stats.ctSideTotalEnemyFlashDuration,
            totalTradesReceived: stats.ctSideTotalTradesReceived,
            clutchesAttempted: stats.ctSideClutchesAttempted,
            clutchesWon: stats.ctSideClutchesWon,
            totalDeaths: stats.ctSideTotalDeaths,
            roundsWith3K: stats.ctSideRoundsWith3K,
            roundsWith4K: stats.ctSideRoundsWith4K,
            roundsWith5K: stats.ctSideRoundsWith5K,
            roundsOnFullBuy: stats.ctSideRoundsOnFullBuy,
            roundsOnFullBuyWon: stats.ctSideRoundsOnFullBuyWon,
            roundsOnHalfBuy: stats.ctSideRoundsOnHalfBuy,
            roundsOnHalfBuyWon: stats.ctSideRoundsOnHalfBuyWon,
            roundsOnEco: stats.ctSideRoundsOnEco,
            roundsOnEcoWon: stats.ctSideRoundsOnEcoWon,
            roundsVsFullBuy: stats.ctSideRoundsVsFullBuy,
            roundsVsFullBuyWon: stats.ctSideRoundsVsFullBuyWon,
            roundsVsHalfBuy: stats.ctSideRoundsVsHalfBuy,
            roundsVsHalfBuyWon: stats.ctSideRoundsVsHalfBuyWon,
            roundsVsEco: stats.ctSideRoundsVsEco,
            roundsVsEcoWon: stats.ctSideRoundsVsEcoWon,
            bombPlantsFor: 0,
            roundsWonAfterPlant: 0,
            bombPlantsAgainst: stats.ctSideBombPlantsAgainst,
            roundsWonAfterEnemyPlant: stats.ctSideRoundsWonAfterEnemyPlant,
        };
    }
    // 'all' / 'overall'
    return {
        roundsPlayed: stats.roundsPlayed,
        roundsWon: stats.roundsWon,
        roundsWithFirstKill: stats.roundsWithFirstKill,
        roundsWonAfterFirstKill: stats.roundsWonAfterFirstKill,
        roundsWithFirstDeath: stats.roundsWithFirstDeath,
        roundsWonAfterFirstDeath: stats.roundsWonAfterFirstDeath,
        pistolRoundsPlayed: stats.pistolRoundsPlayed,
        pistolRoundsWon: stats.pistolRoundsWon,
        secondRoundsPlayed: stats.secondRoundsPlayed,
        secondRoundsWon: stats.secondRoundsWon,
        roundsWithTeamAwp: stats.roundsWithTeamAwp,
        roundsWonWithTeamAwp: stats.roundsWonWithTeamAwp,
        roundsWithoutTeamAwp: stats.roundsWithoutTeamAwp,
        roundsWonWithoutTeamAwp: stats.roundsWonWithoutTeamAwp,
        roundsVsEnemyAwp: stats.roundsVsEnemyAwp,
        roundsWonVsEnemyAwp: stats.roundsWonVsEnemyAwp,
        roundsVsNoEnemyAwp: stats.roundsVsNoEnemyAwp,
        roundsWonVsNoEnemyAwp: stats.roundsWonVsNoEnemyAwp,
        totalEnemiesFlashed: stats.totalEnemiesFlashed,
        totalEnemyFlashDuration: stats.totalEnemyFlashDuration,
        totalTradesReceived: stats.totalTradesReceived,
        clutchesAttempted: stats.clutchesAttempted,
        clutchesWon: stats.clutchesWon,
        totalDeaths: stats.totalDeaths,
        roundsWith3K: stats.roundsWith3K,
        roundsWith4K: stats.roundsWith4K,
        roundsWith5K: stats.roundsWith5K,
        roundsOnFullBuy: stats.roundsOnFullBuy,
        roundsOnFullBuyWon: stats.roundsOnFullBuyWon,
        roundsOnHalfBuy: stats.roundsOnHalfBuy,
        roundsOnHalfBuyWon: stats.roundsOnHalfBuyWon,
        roundsOnEco: stats.roundsOnEco,
        roundsOnEcoWon: stats.roundsOnEcoWon,
        roundsVsFullBuy: stats.roundsVsFullBuy,
        roundsVsFullBuyWon: stats.roundsVsFullBuyWon,
        roundsVsHalfBuy: stats.roundsVsHalfBuy,
        roundsVsHalfBuyWon: stats.roundsVsHalfBuyWon,
        roundsVsEco: stats.roundsVsEco,
        roundsVsEcoWon: stats.roundsVsEcoWon,
        bombPlantsFor: stats.bombPlantsForTeam,
        roundsWonAfterPlant: stats.roundsWonAfterTeamPlant,
        bombPlantsAgainst: stats.bombPlantsAgainstTeam,
        roundsWonAfterEnemyPlant: stats.roundsWonAfterEnemyPlant,
    };
}

function pct(won: number, total: number): string {
    return total > 0 ? ((won / total) * 100).toFixed(1) + '%' : '-';
}

function perRound(value: number, rounds: number): string {
    return rounds > 0 ? (value / rounds).toFixed(2) : '-';
}

const TeamStats: React.FC<TeamStatsProps> = ({ season, teamId: squadId }) => {
    const params = useParams<{ teamId: string }>();
    const teamId = squadId ?? params.teamId;
    const [stats, setStats] = useState<TeamMatchStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedSide, setSelectedSide] = useState<Side>('all');
    const [selectedMap, setSelectedMap] = useState<string>('');
    const [playedMaps, setPlayedMaps] = useState<string[]>([]);

    useEffect(() => {
        if (!teamId || !season) return;
        fetchTeamPlayedMaps(teamId, season).then(setPlayedMaps);
    }, [teamId, season]);

    useEffect(() => {
        if (!teamId || !season) return;
        setLoading(true);
        fetchTeamStats(teamId, season, selectedMap || undefined)
            .then(data => setStats(data))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, [teamId, season, selectedMap]);

    if (loading) return <Loading />;

    const hasData = stats && stats.roundsPlayed > 0;
    const d = hasData ? getSideStats(stats, selectedSide) : null;

    return (
        <div className="mt-3">

            {/* Controls */}
            <div className="team-stats-controls">
                <SideSelector selectedSide={selectedSide} onSideChange={(s) => setSelectedSide(s)} />
                <select
                    className="map-filter-select"
                    value={selectedMap}
                    onChange={(e) => setSelectedMap(e.target.value)}
                >
                    <option value="">Alle maps</option>
                    {playedMaps.map(map => (
                        <option key={map} value={map}>{mapMappings.get(map) || map.replace('de_', '').replace(/^\w/, c => c.toUpperCase())}</option>
                    ))}
                </select>
            </div>

            {!hasData && (
                <div className="team-stats-no-data">Ingen statistik tilgængelig for dette filter.</div>
            )}

            {hasData && d && (<>
            {/* Section 1: Overview Cards */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Overblik</div>
                <div className="row g-2">
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.roundsWon, d.roundsPlayed)}</div>
                            <div className="stat-card-label">Win Rate</div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.roundsWonAfterFirstKill, d.roundsWithFirstKill)}</div>
                            <div className="stat-card-label">First Kill Win%</div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.pistolRoundsWon, d.pistolRoundsPlayed)}</div>
                            <div className="stat-card-label">Pistol Win%</div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.totalTradesReceived, d.totalDeaths)}</div>
                            <div className="stat-card-label">Trade %</div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.clutchesWon, d.clutchesAttempted)}</div>
                            <div className="stat-card-label">Clutch Win%</div>
                        </div>
                    </div>
                    <div className="col-lg-2 col-md-4 col-4">
                        <div className="stat-card stat-card-compact text-center p-2">
                            <div className="stat-card-value">{pct(d.roundsOnFullBuyWon, d.roundsOnFullBuy)}</div>
                            <div className="stat-card-label">Full Buy Win%</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 2: Opening Duels */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Opening Duels</div>
                <div className="team-stats-card">
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">First Kill Rate</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWithFirstKill, d.roundsPlayed)}
                            <span className="team-stats-row-sub">({d.roundsWithFirstKill}/{d.roundsPlayed})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Win efter First Kill (5v4)</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonAfterFirstKill, d.roundsWithFirstKill)}
                            <span className="team-stats-row-sub">({d.roundsWonAfterFirstKill}/{d.roundsWithFirstKill})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">First Death Rate</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWithFirstDeath, d.roundsPlayed)}
                            <span className="team-stats-row-sub">({d.roundsWithFirstDeath}/{d.roundsPlayed})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Win efter First Death (4v5)</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonAfterFirstDeath, d.roundsWithFirstDeath)}
                            <span className="team-stats-row-sub">({d.roundsWonAfterFirstDeath}/{d.roundsWithFirstDeath})</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Section: Køb & Site */}
            {teamId && (
                <div className="team-stats-section">
                    <div className="team-stats-section-title">Køb &amp; Site</div>
                    {selectedMap ? (
                        <BuySiteWinRatePanel
                            teamId={teamId}
                            seasons={season > 0 ? [season] : []}
                            mapName={selectedMap}
                            side={selectedSide}
                        />
                    ) : (
                        <div className="team-stats-card" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', textAlign: 'center' }}>
                            Vælg et specifikt map ovenfor for at se win-rate per buy &amp; site.
                        </div>
                    )}
                </div>
            )}

            {/* Section 3: Economy */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Økonomi</div>
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="team-stats-card">
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                                Holdets Økonomi
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Full Buy</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsOnFullBuyWon, d.roundsOnFullBuy)}
                                    <span className="team-stats-row-sub">({d.roundsOnFullBuyWon}/{d.roundsOnFullBuy})</span>
                                </span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Half Buy</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsOnHalfBuyWon, d.roundsOnHalfBuy)}
                                    <span className="team-stats-row-sub">({d.roundsOnHalfBuyWon}/{d.roundsOnHalfBuy})</span>
                                </span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Eco</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsOnEcoWon, d.roundsOnEco)}
                                    <span className="team-stats-row-sub">({d.roundsOnEcoWon}/{d.roundsOnEco})</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="team-stats-card">
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                                Mod Fjende
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">vs Full Buy</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsVsFullBuyWon, d.roundsVsFullBuy)}
                                    <span className="team-stats-row-sub">({d.roundsVsFullBuyWon}/{d.roundsVsFullBuy})</span>
                                </span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">vs Half Buy</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsVsHalfBuyWon, d.roundsVsHalfBuy)}
                                    <span className="team-stats-row-sub">({d.roundsVsHalfBuyWon}/{d.roundsVsHalfBuy})</span>
                                </span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">vs Eco</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsVsEcoWon, d.roundsVsEco)}
                                    <span className="team-stats-row-sub">({d.roundsVsEcoWon}/{d.roundsVsEco})</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 4: AWP Impact */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">AWP Impact</div>
                <div className="team-stats-card">
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Med AWP Win Rate</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonWithTeamAwp, d.roundsWithTeamAwp)}
                            <span className="team-stats-row-sub">({d.roundsWonWithTeamAwp}/{d.roundsWithTeamAwp})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Uden AWP Win Rate</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonWithoutTeamAwp, d.roundsWithoutTeamAwp)}
                            <span className="team-stats-row-sub">({d.roundsWonWithoutTeamAwp}/{d.roundsWithoutTeamAwp})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">vs Fjende AWP Win Rate</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonVsEnemyAwp, d.roundsVsEnemyAwp)}
                            <span className="team-stats-row-sub">({d.roundsWonVsEnemyAwp}/{d.roundsVsEnemyAwp})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">vs Ingen Fjende AWP</span>
                        <span className="team-stats-row-value">
                            {pct(d.roundsWonVsNoEnemyAwp, d.roundsVsNoEnemyAwp)}
                            <span className="team-stats-row-sub">({d.roundsWonVsNoEnemyAwp}/{d.roundsVsNoEnemyAwp})</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Section 5: Post-Plant */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Post-Plant</div>
                <div className="team-stats-card">
                    {(selectedSide === 'all' || selectedSide === 'overall' || selectedSide === 't') && d.bombPlantsFor > 0 && (
                        <>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Bomb Plants</span>
                                <span className="team-stats-row-value">{d.bombPlantsFor}</span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">After-Plant Win Rate</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsWonAfterPlant, d.bombPlantsFor)}
                                    <span className="team-stats-row-sub">({d.roundsWonAfterPlant}/{d.bombPlantsFor})</span>
                                </span>
                            </div>
                        </>
                    )}
                    {(selectedSide === 'all' || selectedSide === 'overall' || selectedSide === 'ct') && d.bombPlantsAgainst > 0 && (
                        <>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Fjende Bomb Plants</span>
                                <span className="team-stats-row-value">{d.bombPlantsAgainst}</span>
                            </div>
                            <div className="team-stats-row">
                                <span className="team-stats-row-label">Retake Win Rate</span>
                                <span className="team-stats-row-value">
                                    {pct(d.roundsWonAfterEnemyPlant, d.bombPlantsAgainst)}
                                    <span className="team-stats-row-sub">({d.roundsWonAfterEnemyPlant}/{d.bombPlantsAgainst})</span>
                                </span>
                            </div>
                        </>
                    )}
                    {d.bombPlantsFor === 0 && d.bombPlantsAgainst === 0 && (
                        <div className="team-stats-row">
                            <span className="team-stats-row-label" style={{ color: 'rgba(255,255,255,0.4)' }}>Ingen post-plant data</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Section 6: Utility & Trades */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Utility & Trades</div>
                <div className="team-stats-card">
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Enemies Flashed / Runde</span>
                        <span className="team-stats-row-value">{perRound(d.totalEnemiesFlashed, d.roundsPlayed)}</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Flash Varighed / Runde</span>
                        <span className="team-stats-row-value">{perRound(d.totalEnemyFlashDuration, d.roundsPlayed)}s</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Trade %</span>
                        <span className="team-stats-row-value">
                            {pct(d.totalTradesReceived, d.totalDeaths)}
                            <span className="team-stats-row-sub">({d.totalTradesReceived}/{d.totalDeaths})</span>
                        </span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Deaths / Runde</span>
                        <span className="team-stats-row-value">{perRound(d.totalDeaths, d.roundsPlayed)}</span>
                    </div>
                </div>
            </div>

            {/* Section 7: Multi-kills & Clutches */}
            <div className="team-stats-section">
                <div className="team-stats-section-title">Multi-kills & Clutches</div>
                <div className="team-stats-card">
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">3K Runder</span>
                        <span className="team-stats-row-value">{d.roundsWith3K}</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">4K Runder</span>
                        <span className="team-stats-row-value">{d.roundsWith4K}</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Ace Runder</span>
                        <span className="team-stats-row-value">{d.roundsWith5K}</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Clutch Forsøg</span>
                        <span className="team-stats-row-value">{d.clutchesAttempted}</span>
                    </div>
                    <div className="team-stats-row">
                        <span className="team-stats-row-label">Clutch Wins</span>
                        <span className="team-stats-row-value">
                            {d.clutchesWon}
                            <span className="team-stats-row-sub">({pct(d.clutchesWon, d.clutchesAttempted)})</span>
                        </span>
                    </div>
                </div>
            </div>
            </>)}
        </div>
    );
};

export default TeamStats;
